import { useMemo } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/shared/widgets/carousel';
import { HorizontalScrollRow } from '@/shared/components/HorizontalScrollRow';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import type { SimilarDesignItem } from '../../domain/models/SimilarDesignItem';
import { shuffleItems } from '../../utils/shuffleItems';
import { SimilarDesignCard } from './SimilarDesignCard';

interface SimilarDesignsSectionProps {
  items: SimilarDesignItem[] | null;
  isLoading: boolean;
  currentDesignId: number;
  onDesignClick?: (designId: number, designName: string) => void;
}

export function SimilarDesignsSection({
  items,
  isLoading,
  currentDesignId,
  onDesignClick,
}: SimilarDesignsSectionProps) {
  const isDesktop = useIsDesktop();
  const filteredItems = useMemo(() => {
    const list = (items ?? []).filter((item) => item.designId !== currentDesignId);
    return shuffleItems(list);
  }, [items, currentDesignId]);

  if (!isLoading && filteredItems.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
        </div>
      )}

      {!isLoading && filteredItems.length > 0 && (
        <>
          <div className="flex flex-col items-center justify-center text-center mb-2">
            <h2 className="nh-section-title px-4 py-3 text-center capitalize leading-none tracking-[0.01em] font-serif text-[#0D2750] flex items-center justify-center gap-3 !text-[16px] md:!text-[20px]">
              <span className="font-serif">Similar Designs</span>
            </h2>
          </div>
          {isDesktop ? (
            <HorizontalScrollRow className="md:gap-4 md:pb-3">
              {filteredItems.map((item) => (
                <div key={item.designId} className="w-[calc((100vw-30px)/2.33)] md:w-[calc((100vw-97px)/4.33)] lg:w-[265.7px] shrink-0">
                  <SimilarDesignCard item={item} onDesignClick={onDesignClick} />
                </div>
              ))}
            </HorizontalScrollRow>
          ) : (
            <Carousel opts={{ align: 'start', dragFree: true }}>
              <CarouselContent className="-ml-3 md:-ml-4">
                {filteredItems.map((item) => (
                  <CarouselItem key={item.designId} className="basis-[calc((100vw-30px)/2.33)] md:basis-[calc((100vw-97px)/4.33)] lg:basis-[265.7px] pl-3 md:pl-4">
                    <SimilarDesignCard item={item} onDesignClick={onDesignClick} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          )}
        </>
      )}
    </div>
  );
}
